import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CropFreeIcon from '@mui/icons-material/CropFree';

/** e.g. desk + 1 → "desk-1"; Lane + 1 → "Lane 1" */
const formatZoneLabel = (noun, key) => {
  const n = String(noun || 'Lane').trim() || 'Lane';
  const k = String(key ?? '').trim();
  if (!k) return n;
  if (/^workstation$/i.test(n)) return `Workstation-${k}`;
  if (/^desk$/i.test(n)) return `Desk-${k}`;
  return `${n} ${k}`;
};

/**
 * ROIDrawer Component
 * Allows users to draw ROI (Region of Interest) rectangles on a camera frame.
 * 
 * Props:
 * - open: boolean - Whether the dialog is open
 * - onClose: function - Called when dialog is closed
 * - cameraId: string - The camera ID to draw ROI for
 * - cameraType: string - "IN" or "OUT"
 * - cameraLabel: string - Label for display
 * - imageUrl: string - Base64 data URL of the frame image
 * - existingRoi: object - Existing ROI {x, y, w, h} or null
 * - existingLaneRois: object - Existing lane ROIs
 * - laneCount: number - Number of lanes (default: 0)
 * - onLaneCountSet: function - Persist lane count for current camera type
 * - onSave: function - Called with ROI data when saved
 * - onFetchFrame: function - Called to fetch a fresh frame
 * - loading: boolean - Whether data is being loaded/saved
 */
const ROIDrawer = ({
  open,
  onClose,
  cameraId,
  cameraType,
  cameraLabel,
  imageUrl,
  existingRoi,
  existingLaneRois,
  laneCount = 0,
  onLaneCountSet,
  onSave,
  onFetchFrame,
  loading = false,
  zoneNoun = 'Lane',
  zoneNounPlural = 'Lanes',
  showPersonOverlap = false,
  existingLaneOverlapRatios = null,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  const [savedRect, setSavedRect] = useState(existingRoi || null);
  const [laneRois, setLaneRois] = useState({});
  const [selectedLane, setSelectedLane] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [error, setError] = useState(null);
  const [numberOfLanes, setNumberOfLanes] = useState(String(laneCount || 0));
  const [settingLaneCount, setSettingLaneCount] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [pendingRoiData, setPendingRoiData] = useState(null);
  const [overlapByLane, setOverlapByLane] = useState({});

  const laneCountInt = Math.max(0, parseInt(numberOfLanes || 0, 10) || 0);

  const normalizeLaneRois = useCallback((input) => {
    if (!input || typeof input !== 'object') return {};
    const out = {};
    Object.entries(input).forEach(([key, value]) => {
      const lane = parseInt(String(key), 10);
      if (!lane || lane <= 0) return;
      let rect = null;
      if (Array.isArray(value) && value.length === 4) {
        rect = { x: value[0], y: value[1], w: value[2], h: value[3] };
      } else if (value && typeof value === 'object') {
        rect = value;
      }
      if (!rect) return;
      const x = parseInt(rect.x, 10);
      const y = parseInt(rect.y, 10);
      const w = parseInt(rect.w, 10);
      const h = parseInt(rect.h, 10);
      if ([x, y, w, h].some((n) => Number.isNaN(n)) || x < 0 || y < 0 || w <= 0 || h <= 0) return;
      out[String(lane)] = { x, y, w, h };
    });
    return out;
  }, []);

  const computeUnionRect = useCallback((roisMap) => {
    const rects = Object.values(roisMap || {});
    if (!rects.length) return null;
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = 0;
    let maxY = 0;
    rects.forEach((r) => {
      minX = Math.min(minX, r.x);
      minY = Math.min(minY, r.y);
      maxX = Math.max(maxX, r.x + r.w);
      maxY = Math.max(maxY, r.y + r.h);
    });
    const w = maxX - minX;
    const h = maxY - minY;
    if (w <= 0 || h <= 0) return null;
    return { x: minX, y: minY, w, h };
  }, []);

  // Load existing ROI when dialog opens or existingRoi changes
  useEffect(() => {
    if (!open) return;
    setNumberOfLanes(String(laneCount || 0));
  }, [open, laneCount]);

  useEffect(() => {
    if (!open || !showPersonOverlap) return;
    const next = {};
    const source = existingLaneOverlapRatios && typeof existingLaneOverlapRatios === 'object'
      ? existingLaneOverlapRatios
      : {};
    Object.entries(source).forEach(([key, value]) => {
      const ratio = Number(value);
      if (!Number.isFinite(ratio)) return;
      next[String(key)] = String(Math.round(ratio * 100));
    });
    setOverlapByLane(next);
  }, [open, showPersonOverlap, existingLaneOverlapRatios]);

  useEffect(() => {
    if (!open) return;
    const normalizedLaneRois = normalizeLaneRois(existingLaneRois);
    setLaneRois(normalizedLaneRois);
    if (laneCountInt > 0) {
      setSelectedLane((prev) => {
        const next = Math.min(Math.max(1, prev || 1), laneCountInt);
        return next;
      });
      setSavedRect(null);
    } else if (existingRoi) {
      setSavedRect(existingRoi);
    } else {
      setSavedRect(null);
    }
  }, [open, existingRoi, existingLaneRois, laneCountInt, normalizeLaneRois]);

  useEffect(() => {
    if (!open || laneCountInt <= 0) return;
    setSelectedLane((prev) => Math.min(Math.max(1, prev || 1), laneCountInt));
  }, [open, laneCountInt]);

  useEffect(() => {
    if (!open || laneCountInt <= 0) return;
    setSavedRect(laneRois[String(selectedLane)] || null);
  }, [open, laneCountInt, selectedLane, laneRois]);

  // Load image and setup canvas when imageUrl changes
  useEffect(() => {
    if (!open || !imageUrl || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate display scale to fit in container
      const container = containerRef.current;
      const maxWidth = container ? container.clientWidth - 32 : 800;
      const maxHeight = 600;

      let scale = 1;
      if (img.width > maxWidth) {
        scale = maxWidth / img.width;
      }
      if (img.height * scale > maxHeight) {
        scale = maxHeight / img.height;
      }

      const displayWidth = Math.floor(img.width * scale);
      const displayHeight = Math.floor(img.height * scale);

      // Preserve the original frame resolution in the canvas so saving the
      // preview does not downscale the underlying image.
      canvas.width = img.width;
      canvas.height = img.height;
      setImageSize({ width: img.width, height: img.height });
      setDisplaySize({ width: displayWidth, height: displayHeight });

      // Draw the image
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, img.width, img.height);
      setImageLoaded(true);

      // Draw existing ROI if available
      if (savedRect) {
        drawRect(ctx, savedRect, 'green');
      }
    };

    img.onerror = () => {
      setError('Failed to load camera frame image');
      setImageLoaded(false);
    };

    img.src = imageUrl;
  }, [imageUrl, open, savedRect]);

  // Redraw canvas when savedRect or currentRect changes
  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || !imageUrl) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      if (laneCountInt > 0) {
        for (let lane = 1; lane <= laneCountInt; lane += 1) {
          const laneRect = laneRois[String(lane)];
          if (!laneRect) continue;
          const isSelected = lane === selectedLane;
          drawRect(ctx, laneRect, isSelected ? '#ffff00' : '#00ff00', formatZoneLabel(zoneNoun, lane));
        }
      } else if (savedRect) {
        drawRect(ctx, savedRect, '#00ff00', 'ROI');
      }

      // Draw current drawing rect in blue (preview)
      if (currentRect) {
        const previewLabel = laneCountInt > 0 ? formatZoneLabel(zoneNoun, selectedLane) : 'ROI';
        drawRect(ctx, currentRect, '#0088ff', previewLabel);
      }
    };

    img.src = imageUrl;
  }, [savedRect, currentRect, imageLoaded, imageUrl, laneCountInt, laneRois, selectedLane, zoneNoun]);

  // Draw rectangle on canvas
  const drawRect = (ctx, rect, color, label = 'ROI') => {
    if (!rect) return;

    const x = Math.floor(rect.x);
    const y = Math.floor(rect.y);
    const w = Math.floor(rect.w);
    const h = Math.floor(rect.h);

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.strokeRect(x, y, w, h);

    // Draw label
    ctx.fillStyle = color;
    ctx.font = '14px Arial';
    ctx.fillText(label, x + 5, y + 18);

    // Draw center point
    const cx = x + w / 2;
    const cy = y + h / 2;
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, 2 * Math.PI);
    ctx.fillStyle = 'red';
    ctx.fill();
  };

  // Get mouse position relative to canvas in original image coordinates
  const getMousePos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    return { x: Math.floor(x), y: Math.floor(y) };
  }, []);

  // Mouse event handlers
  const handleMouseDown = useCallback((e) => {
    if (!imageLoaded) return;
    const pos = getMousePos(e);
    if (!pos) return;

    setIsDrawing(true);
    setStartPoint(pos);
    setCurrentRect(null);
  }, [imageLoaded, getMousePos]);

  const handleMouseMove = useCallback((e) => {
    if (!isDrawing || !startPoint) return;
    const pos = getMousePos(e);
    if (!pos) return;

    // Calculate rectangle from start to current position
    const x = Math.min(startPoint.x, pos.x);
    const y = Math.min(startPoint.y, pos.y);
    const w = Math.abs(pos.x - startPoint.x);
    const h = Math.abs(pos.y - startPoint.y);

    // Clamp to image bounds
    const clampedX = Math.max(0, Math.min(x, imageSize.width));
    const clampedY = Math.max(0, Math.min(y, imageSize.height));
    const clampedW = Math.min(w, imageSize.width - clampedX);
    const clampedH = Math.min(h, imageSize.height - clampedY);

    setCurrentRect({ x: clampedX, y: clampedY, w: clampedW, h: clampedH });
  }, [isDrawing, startPoint, getMousePos, imageSize]);

  const handleMouseUp = useCallback(() => {
    if (!isDrawing) return;

    // Save the current rect if it has valid dimensions
    if (currentRect && currentRect.w > 10 && currentRect.h > 10) {
      if (laneCountInt > 0) {
        setLaneRois((prev) => ({
          ...prev,
          [String(selectedLane)]: currentRect,
        }));
      }
      setSavedRect(currentRect);
    }

    setIsDrawing(false);
    setStartPoint(null);
    setCurrentRect(null);
  }, [isDrawing, currentRect, laneCountInt, selectedLane]);

  // Clear ROI
  const handleClear = () => {
    if (laneCountInt > 0) {
      setLaneRois((prev) => {
        const next = { ...prev };
        delete next[String(selectedLane)];
        return next;
      });
    }
    setSavedRect(null);
    setCurrentRect(null);
  };

  const overlapRatioForLane = useCallback((laneKey) => {
    const raw = overlapByLane[String(laneKey)];
    const percent = raw === undefined || raw === '' ? 30 : Number(raw);
    if (!Number.isFinite(percent) || percent < 1 || percent > 100) {
      setError(
        `Minimum person overlap for ${formatZoneLabel(zoneNoun, laneKey)} must be between 1 and 100`
      );
      return null;
    }
    return Math.round(percent) / 100;
  }, [overlapByLane, zoneNoun]);

  const buildOverlapRatios = useCallback((laneKeys) => {
    const ratios = {};
    for (const laneKey of laneKeys) {
      const ratio = overlapRatioForLane(laneKey);
      if (ratio == null) return null;
      ratios[String(laneKey)] = ratio;
    }
    return ratios;
  }, [overlapRatioForLane]);

  const buildRoiSavePayload = useCallback(() => {
    if (laneCountInt < 0) {
      setError(`Number of ${zoneNounPlural.toLowerCase()} must be 0 or greater`);
      return null;
    }

    if (laneCountInt > 1) {
      const activeLaneRois = {};
      const missing = [];
      for (let lane = 1; lane <= laneCountInt; lane += 1) {
        const rect = laneRois[String(lane)];
        if (!rect) {
          missing.push(lane);
        } else {
          activeLaneRois[String(lane)] = rect;
        }
      }
      if (missing.length > 0) {
        setError(
          `Please draw ROI for: ${missing.map((lane) => formatZoneLabel(zoneNoun, lane)).join(', ')}`
        );
        return null;
      }
      const unionRect = computeUnionRect(activeLaneRois);
      if (!unionRect) {
        setError(`Could not compute ROI from ${zoneNoun.toLowerCase()} rectangles`);
        return null;
      }
      const payload = {
        rect: [unionRect.x, unionRect.y, unionRect.w, unionRect.h],
        x: unionRect.x,
        y: unionRect.y,
        w: unionRect.w,
        h: unionRect.h,
        lane_rois: activeLaneRois,
        number_of_lane: laneCountInt,
        frame_width: imageSize.width || undefined,
        frame_height: imageSize.height || undefined,
      };
      if (showPersonOverlap) {
        const ratios = buildOverlapRatios(Object.keys(activeLaneRois));
        if (!ratios) return null;
        payload.lane_overlap_ratios = ratios;
      }
      return payload;
    }

    if (!savedRect) {
      setError('Please draw an ROI rectangle');
      return null;
    }

    const payload = {
      rect: [savedRect.x, savedRect.y, savedRect.w, savedRect.h],
      x: savedRect.x,
      y: savedRect.y,
      w: savedRect.w,
      h: savedRect.h,
      number_of_lane: laneCountInt,
      frame_width: imageSize.width || undefined,
      frame_height: imageSize.height || undefined,
      ...(laneCountInt === 0 ? { lane_rois: {} } : {}),
    };
    if (showPersonOverlap && laneCountInt === 1) {
      payload.lane_rois = { '1': savedRect };
      const ratios = buildOverlapRatios(['1']);
      if (!ratios) return null;
      payload.lane_overlap_ratios = ratios;
    }
    if (showPersonOverlap && laneCountInt === 0) {
      payload.lane_overlap_ratios = {};
    }
    return payload;
  }, [
    savedRect,
    laneCountInt,
    laneRois,
    computeUnionRect,
    zoneNoun,
    zoneNounPlural,
    imageSize,
    showPersonOverlap,
    buildOverlapRatios,
  ]);

  const handleCloseSaveConfirm = () => {
    if (loading) return;
    setSaveConfirmOpen(false);
    setPendingRoiData(null);
  };

  const handleOpenSaveConfirm = useCallback(() => {
    setError(null);
    const payload = buildRoiSavePayload();
    if (!payload) return;
    setPendingRoiData(payload);
    setSaveConfirmOpen(true);
  }, [buildRoiSavePayload]);

  const handleConfirmSave = () => {
    if (!pendingRoiData) return;
    onSave(pendingRoiData);
    setSaveConfirmOpen(false);
    setPendingRoiData(null);
  };

  // Fetch fresh frame
  const handleRefreshFrame = () => {
    setError(null);
    setImageLoaded(false);
    if (onFetchFrame) {
      onFetchFrame();
    }
  };

  const handleSetLaneCount = async () => {
    const parsed = parseInt(numberOfLanes || '0', 10);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError(`Number of ${zoneNounPlural.toLowerCase()} must be 0 or greater`);
      return;
    }
    setError(null);
    if (!onLaneCountSet) return;
    try {
      setSettingLaneCount(true);
      await onLaneCountSet(parsed);
      if (parsed === 0) {
        setLaneRois({});
      }
    } catch (err) {
      setError(err.message || 'Failed to set lane count');
    } finally {
      setSettingLaneCount(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    setCurrentRect(null);
    setIsDrawing(false);
    setStartPoint(null);
    setError(null);
    setSaveConfirmOpen(false);
    setPendingRoiData(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: '70vh',
          maxHeight: '95vh',
          borderRadius: 3,
          m: { xs: 1, sm: 2 },
        }
      }}
    >
      <DialogTitle sx={{ 
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50'
        }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: cameraType === 'IN' ? '#eef2ff' : '#fef3c7',
                  color: cameraType === 'IN' ? '#40518A' : '#d97706',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CropFreeIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Draw ROI - {cameraLabel || cameraType}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click and drag on the image to draw a Region of Interest (ROI). The ROI defines the zone where face detection will be triggered.
                </Typography>
              </Box>
            </Stack>
            
          </Stack>
        </DialogTitle>

      <DialogContent ref={containerRef} sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
            Number of {zoneNounPlural}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
            <TextField
              type="number"
              value={numberOfLanes}
              onChange={(e) => setNumberOfLanes(e.target.value)}
              InputProps={{ inputProps: { min: 0 } }}
              size="small"
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleSetLaneCount}
              disabled={loading || settingLaneCount}
              sx={{ minWidth: 96, height: 40 }}
            >
              {settingLaneCount ? '...' : 'SET'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Enter 0 for single ROI or a number for multiple {zoneNounPlural.toLowerCase()}.
          </Typography>
        </Box>

        {loading && !imageUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !imageUrl && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Stack alignItems="center" spacing={2}>
              <Typography color="text.secondary">No frame available</Typography>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshFrame}>
                Fetch Frame
              </Button>
            </Stack>
          </Box>
        )}

        {imageUrl && (
          <Box sx={{ position: 'relative' }}>
            {/* Preview Header with Lane Selection */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2,
              px: 1
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Preview
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 1 }} alignItems={{ sm: 'center' }}>
                {/* Lane Selection Dropdown */}
                {laneCountInt > 1 && (
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>{zoneNoun}</InputLabel>
                    <Select
                      value={selectedLane}
                      label={zoneNoun}
                      onChange={(e) => {
                        const newLane = parseInt(e.target.value);
                        setSelectedLane(newLane);
                        // Load existing ROI for this lane if available
                        if (existingLaneRois && existingLaneRois[newLane]) {
                          setSavedRect(existingLaneRois[newLane]);
                        } else {
                          // Clear ROI for new lane
                          setSavedRect(null);
                        }
                      }}
                    >
                      {Array.from({ length: laneCountInt }, (_, i) => (
                        <MenuItem key={i} value={i + 1}>
                          {formatZoneLabel(zoneNoun, i + 1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {showPersonOverlap && laneCountInt >= 1 && (
                  <TextField
                    type="number"
                    size="small"
                    label="Minimum person overlap (%)"
                    value={overlapByLane[String(selectedLane)] ?? '30'}
                    onChange={(e) => {
                      const value = e.target.value;
                      setOverlapByLane((prev) => ({
                        ...prev,
                        [String(selectedLane)]: value,
                      }));
                    }}
                    inputProps={{ min: 1, max: 100, step: 1 }}
                    sx={{ width: { xs: '100%', sm: 240 } }}
                  />
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshFrame}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ClearIcon />}
                  onClick={handleClear}
                  disabled={loading || !savedRect}
                  sx={{ 
                    color: 'error.main',
                    borderColor: 'error.main',
                    '&:hover': {
                      borderColor: 'error.main',
                      bgcolor: 'rgba(211, 47, 47, 0.04)',
                    }
                  }}
                >
                  Clear
                </Button>
              </Stack>
            </Box>

            {/* Canvas Container */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'grey.900',
                borderRadius: 1,
                p: 1,
                minHeight: { xs: 250, sm: 300, md: 400 },
                maxHeight: { xs: '60vh', sm: '65vh', md: '70vh' },
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  cursor: isDrawing ? 'crosshair' : 'crosshair',
                  width: displaySize.width > 0 ? `${displaySize.width}px` : 'auto',
                  height: displaySize.height > 0 ? `${displaySize.height}px` : 'auto',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  border: '1px solid #666',
                  objectFit: 'contain',
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              />
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    borderRadius: 1,
                  }}
                >
                  <CircularProgress sx={{ color: 'white' }} />
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* ROI Information Block */}
        {savedRect && (
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'divider' 
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              ROI Information
            </Typography>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Type:</strong> {laneCountInt > 1 ? `${formatZoneLabel(zoneNoun, selectedLane)} ROI` : 'Single ROI'}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total {zoneNounPlural}:</strong> {laneCountInt}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Position:</strong> X: {savedRect.x}, Y: {savedRect.y}
                </Typography>
              </Box>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Size:</strong> W: {savedRect.w}, H: {savedRect.h}
                </Typography>
              </Box>
              {showPersonOverlap && laneCountInt >= 1 && (
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Minimum person overlap:</strong>{' '}
                    {overlapByLane[String(selectedLane)] || '30'}%
                  </Typography>
                </Box>
              )}
            </Stack>
            {imageSize.width > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Original image size: {imageSize.width} x {imageSize.height}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Button 
          onClick={handleClose} 
          disabled={loading} 
          variant="outlined"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleOpenSaveConfirm}
          disabled={loading || saveConfirmOpen || (laneCountInt > 0 ? Object.keys(laneRois).length < laneCountInt : !savedRect)}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Save ROI & Detection Cameras
        </Button>
      </DialogActions>

      <Dialog
        open={saveConfirmOpen}
        onClose={handleCloseSaveConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: '#eef2ff',
                color: '#40518A',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SaveIcon sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Save ROI Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {laneCountInt > 1
                  ? `${laneCountInt} ${zoneNounPlural.toLowerCase()} for ${cameraType} camera`
                  : `Single ROI for ${cameraType} camera`}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Are you sure you want to save this ROI?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {cameraLabel || cameraType}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                This ROI will be reflected in the Detect app after restarting the Detect app.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleCloseSaveConfirm}
            variant="outlined"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            {loading ? 'Saving...' : 'Save ROI & Detection Cameras'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ROIDrawer;
